import { confirmAppDialog } from '../services/appDialogs';
import { RazorpayOptions, RazorpayResponse } from '../types/razorpay';

const RAZORPAY_SCRIPT_URL = 'https://checkout.razorpay.com/v1/checkout.js';

const loadNewScript = (): Promise<void> => new Promise((resolve, reject) => {
  const script = document.createElement('script');

  script.src = RAZORPAY_SCRIPT_URL;
  script.async = true;
  script.onload = () => resolve();
  script.onerror = () => reject(new Error('Failed to load Razorpay'));
  document.head.appendChild(script);
});

const waitForExisting = (el: HTMLElement): Promise<void> => new Promise((resolve, reject) => {
  el.addEventListener('load', () => resolve());
  el.addEventListener('error', () => reject(new Error('Failed to load Razorpay')));
});

const ensureRazorpayLoaded = (): Promise<void> => {
  if (window.Razorpay) return Promise.resolve();

  const existing = document.querySelector(`script[src="${RAZORPAY_SCRIPT_URL}"]`) as HTMLElement | null;

  return existing ? waitForExisting(existing) : loadNewScript();
};

const buildMockResponse = (options: RazorpayOptions): RazorpayResponse => {
  const mock: RazorpayResponse = {
    razorpay_payment_id: `pay_mock_${Date.now()}`,
    razorpay_signature: `sig_mock_${Date.now()}`,
  };

  if ('subscription_id' in options) mock.razorpay_subscription_id = options.subscription_id;
  if ('order_id' in options) mock.razorpay_order_id = options.order_id;

  return mock;
};

const isMockCheckout = (options: RazorpayOptions): boolean => (
  !options.key ||
  options.key === 'rzp_test_mock' ||
  options.key.includes('mock') ||
  ('subscription_id' in options && !!options.subscription_id?.includes('mock')) ||
  ('order_id' in options && !!options.order_id?.includes('mock'))
);

const runMockCheckout = (options: RazorpayOptions): void => {
  console.warn('Mock Razorpay Checkout Initiated:', options);

  const mockResponse = buildMockResponse(options);

  setTimeout(async () => {
    if (await confirmAppDialog({
      title: 'Mock payment', message: 'Choose a result for this test payment. No money will be charged.',
      confirmLabel: 'Simulate success', cancelLabel: 'Simulate failure',
    })) {
      options.handler(mockResponse);
    }
  }, 1000);
};

export const openRazorpay = async (options: RazorpayOptions): Promise<void> => {
  if (isMockCheckout(options)) {
    runMockCheckout(options);

    return;
  }

  await ensureRazorpayLoaded();

  const rzp = new window.Razorpay(options);

  rzp.open();
};
